// services/constructionService.ts

import api from './api';
import {
  Construction,
  ConstructionFormData,
  ConstructionListResponse,
  ConstructionResponse,
  ConstructionCreateResponse,
  ConstructionExpense,
  ConstructionVendor,
  ConstructionRemark,
  SearchParams,
  IncompleteWork,
  IncompleteWorksResponse,
  CompletedWorkWithoutInvoiceRequest,
  CompletedWorksWithoutInvoiceRequestResponse,
  CompleteWorkResponse,
  CreateInvoiceRequestResponse,
  WeeklyLoginCheckResponse
} from '../types/construction';
import {
  NormalizedAddress,
  APIAddressSuggestion,
  APIAddressDetails,
  APIAddressSearchResponse
} from '../types/address';
import { useUser } from '../hooks/useUser'; // カスタムフックをインポート


export const constructionService = {
  // 一覧取得
  getAll: async (): Promise<Construction[]> => {
    try {
      const response = await api.get<Construction[] | ConstructionListResponse>('/construction/list');

      // レスポンスの形式をチェックして適切に処理
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return response.data.constructions;
      }
    } catch (error) {
      console.error('工事データ一覧の取得に失敗:', error);
      throw new Error('工事データの取得に失敗しました');
    }
  },

  // 詳細取得
  getById: async (ID: number): Promise<ConstructionFormData> => {
    try {
      const response = await api.get<ConstructionResponse>(`/construction/${ID}`);
      if (!response.data.success || !response.data.data) {
        throw new Error('工事データの取得に失敗しました');
      }

      const apiData = response.data.data;  // APIConstructionData型

      // API形式からフォーム形式へ変換
      const formData: ConstructionFormData = {
        ...apiData,
        expenses: apiData.expenses?.map(expense => ({
          price: expense.Price,
          detail: expense.Detail,
          constructionId: apiData.ID || 0
        })) || [],
        vendors: apiData.vendors?.map(vendor => ({
          price: vendor.Price,
          detail: vendor.Detail,
          constructionId: apiData.ID || 0
        })) || [],
        remarks: apiData.remarks?.map(remark => ({
          remarks: remark.Remarks,
          constructionId: apiData.ID || 0
        })) || []
      };

      return formData;
    } catch (error) {
      console.error('工事データ詳細の取得に失敗:', error);
      throw error;
    }
  },

  // 検索機能
  search: async (params: SearchParams): Promise<Construction[]> => {
    try {
      // クエリパラメータを構築
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const response = await api.get<ConstructionListResponse>(`/construction/search?${queryParams}`);

      if (!response.data || !response.data.constructions) {
        throw new Error('検索結果の取得に失敗しました');
      }

      return response.data.constructions;
    } catch (error) {
      console.error('検索エラー:', error);
      throw error;
    }
  },

  // 新規作成
  // create: async (data: Omit<Construction, 'ID'>): Promise<Construction> => {
  //   try {
  //     const response = await api.post<ConstructionResponse>('/construction/create', data);
  //     if (!response.data.success || !response.data.data) {
  //       throw new Error(response.data.error || '工事データの作成に失敗しました');
  //     }
  //     return response.data.data;
  //   } catch (error) {
  //     console.error('工事データの作成に失敗:', error);
  //     throw error;
  //   }
  // },
  create: async (data: Omit<Construction, 'id'>): Promise<number> => {
    try {
      const response = await api.post<ConstructionCreateResponse>('/construction/create', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '工事データの作成に失敗しました');
      }
      return response.data.data.id;
    } catch (error) {
      console.error('工事データの作成に失敗:', error);
      throw error;
    }
  },

  // 更新
  update: async (ID: number, data: Partial<Construction>): Promise<Construction> => {
    try {

      const response = await api.put<ConstructionResponse>(`/construction/${ID}`, data);

      if (!response.data.success) {
        throw new Error(response.data.error || '工事データの更新に失敗しました');
      }

      if (!response.data.data) {
        throw new Error('更新後のデータが返されませんでした');
      }

      return response.data.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error('工事データの更新に失敗:', error.message);
        throw error;
      }
      // AxiosErrorなど他のエラーの場合
      console.error('工事データの更新に失敗:', error);
      throw new Error('工事データの更新に失敗しました');
    }
  },

  // 削除
  delete: async (ID: number): Promise<boolean> => {
    try {
      const response = await api.delete(`/construction/${ID}`);
      return true; // レスポンスが200なら成功とみなす
    } catch (error) {
      console.error('工事データの削除に失敗:', error);
      throw error;
    }
  },


  // 経費データの登録
  createExpense: async (data: Omit<ConstructionExpense, 'id'>): Promise<ConstructionExpense> => {
    try {
      const response = await api.post<{ success: boolean; data?: ConstructionExpense; error?: string }>(
        '/construction/expense/create',
        data
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '経費データの登録に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      console.error('経費データの登録に失敗:', error);
      throw error;
    }
  },

  // 外注データの登録
  createVendor: async (data: Omit<ConstructionVendor, 'id'>): Promise<ConstructionVendor> => {
    try {
      const response = await api.post<{ success: boolean; data?: ConstructionVendor; error?: string }>(
        '/construction/vendor/create',
        data
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '外注データの登録に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      console.error('外注データの登録に失敗:', error);
      throw error;
    }
  },

  // 備考データの登録
  createRemark: async (data: Omit<ConstructionRemark, 'id'>): Promise<ConstructionRemark> => {
    try {
      const response = await api.post<{ success: boolean; data?: ConstructionRemark; error?: string }>(
        '/construction/remark/create',
        data
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '備考データの登録に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      console.error('備考データの登録に失敗:', error);
      throw error;
    }
  },

  searchAddress: async (searchText: string): Promise<APIAddressSuggestion[]> => {
    try {
      const response = await api.get<{ success: boolean; suggestions: APIAddressSuggestion[] }>(
        `/location/address/suggest?q=${encodeURIComponent(searchText)}`
      );

      if (!response.data.success) {
        throw new Error('住所検索に失敗しました');
      }

      return response.data.suggestions;
    } catch (error) {
      console.error('住所検索エラー:', error);
      throw error;
    }
  },


  // 住所正規化メソッド
  normalizeAddress: async (address: string): Promise<NormalizedAddress> => {
    try {
      const response = await api.post<{ success: boolean; data: NormalizedAddress }>(
        '/location/address/normalize',
        { address }
      );

      if (!response.data || !response.data.success) {
        throw new Error('正規化データが取得できませんでした');
      }

      return response.data.data;
    } catch (error) {
      console.error('住所正規化エラー:', error);
      throw error;
    }
  },

  // 経路情報取得
  getRouteInfo: async (address: string) => {
    try {
      const response = await api.post<{ success: boolean; routeInfo: any }>('/location/route/info', { address });

      if (!response.data || !response.data.success) {
        throw new Error('経路情報の取得に失敗しました');
      }

      return response.data.routeInfo;
    } catch (error) {
      console.error('経路情報取得エラー:', error);
      throw error;
    }
  },

  // 工事データと関連データを一括登録するヘルパーメソッド
  createWithRelatedData: async (
    constructionData: Omit<Construction, 'id'> & { normalizedAddress?: NormalizedAddress },
    expenses: Omit<ConstructionExpense, 'id' | 'constructionId'>[],
    vendors: Omit<ConstructionVendor, 'id' | 'constructionId'>[],
    remarks: Omit<ConstructionRemark, 'id' | 'constructionId'>[]
  ): Promise<Construction> => {
    try {
      // 住所の正規化が必要な場合は実行
      if (!constructionData.normalizedAddress && constructionData.address) {
        constructionData.normalizedAddress = await constructionService.normalizeAddress(constructionData.address);
      }

      const constructionId = await constructionService.create(constructionData);

      if (!constructionId) {
        throw new Error('工事データのIDが取得できませんでした');
      }

      // 関連データが存在する場合のみ登録処理を実行
      // const promises: Promise<any>[] = [];
      // if (expenses && expenses.length > 1) {
      //   promises.push(
      //     ...expenses.map(expense =>
      //       constructionService.createExpense({
      //         ...expense,
      //         constructionId
      //       })
      //     )
      //   );
      // }

      // if (vendors && vendors.length > 1) {
      //   promises.push(
      //     ...vendors.map(vendor =>
      //       constructionService.createVendor({
      //         ...vendor,
      //         constructionId
      //       })
      //     )
      //   );
      // }

      // if (remarks && remarks.length > 1) {
      //   promises.push(
      //     ...remarks.map(remark =>
      //       constructionService.createRemark({
      //         ...remark,
      //         constructionId
      //       })
      //     )
      //   );
      // }

      // // 関連データが存在する場合のみ Promise.all を実行
      // if (promises.length > 0) {
      //   await Promise.all(promises);
      // }

      // 登録された工事データを返却
      return {
        ...constructionData,
        ID: constructionId
      };
    } catch (error) {
      console.error('工事データの一括登録に失敗:', error);
      throw error;
    }
  },

  updateStatus: async (params: { constructionId: number; isCompleted: boolean }) => {
    try {
      const response = await api.put(`/construction/${params.constructionId}/status`, {
        isCompleted: params.isCompleted
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 現場名から経路情報を検索
  searchRouteBySiteName: async (siteName: string) => {
    try {
      const response = await api.get(`/location/route/searchBySiteName?siteName=${encodeURIComponent(siteName)}`);

      if (!response.data) {
        throw new Error('現場名からの経路検索に失敗しました');
      }

      return response.data;
    } catch (error) {
      console.error('現場名検索エラー:', error);
      throw error;
    }
  },

  // 複数の経路情報を取得
  getMultipleRoutes: async (address: string) => {
    try {
      const response = await api.post<{ success: boolean; routes: any[] }>('/location/route/multiple', { address });

      if (!response.data || !response.data.success) {
        throw new Error('経路候補の取得に失敗しました');
      }

      return response.data.routes;
    } catch (error) {
      console.error('経路候補取得エラー:', error);
      throw error;
    }
  },


  // 選択した経路情報を保存
  saveSelectedRoute: async (originalAddress: string, normalizedAddress: string, routeInfo: any) => {
    try {

      const response = await api.post<{ success: boolean; routeId: number; addressId: number }>
        ('/location/route/save', {
          originalAddress,
          normalizedAddress,
          routeInfo
        });

      if (!response.data || !response.data.success) {
        throw new Error('経路情報の保存に失敗しました');
      }

      return response.data;
    } catch (error) {
      console.error('経路保存エラー:', error);
      throw error;
    }
  },


  // 現場名から住所を検索
  searchAddressByKeyword: async (keyword: string) => {
    try {
      const response = await api.get<{ success: boolean; suggestions: any[] }>
        (`/location/address/search-by-site?siteName=${encodeURIComponent(keyword)}`);

      if (!response.data || !response.data.success) {
        throw new Error('住所候補の取得に失敗しました');
      }

      return response.data.suggestions || [];
    } catch (error) {
      console.error('住所キーワード検索エラー:', error);
      throw error;
    }
  },

  // 既存経路情報確認
  checkExistingRouteInfo: async (normalizedAddress: string) => {
    try {
      const response = await api.post<{ success: boolean; exists: boolean; routeInfo?: any }>
        ('/location/route/check-existing', { normalizedAddress });

      if (response.data.success && response.data.exists) {
        return response.data.routeInfo;
      }

      return null;
    } catch (error) {
      console.error('既存経路情報確認エラー:', error);
      return null;
    }
  },

  // 全車種の料金を取得するメソッド
  getAllVehicleTypeTolls: async (address: string, distance: number) => {
    try {
      const response = await api.post<{
        success: boolean;
        tolls: {
          // Light_Vehicle_Toll: number;
          Standard_Vehicle_Toll: number;
          Medium_Vehicle_Toll: number;
          // Large_Vehicle_Toll: number;
        }
      }>('/location/route/all-vehicle-tolls', {
        address,
        distance
      });

      if (!response.data || !response.data.success) {
        throw new Error('車種別料金の取得に失敗しました');
      }

      return response.data.tolls;
    } catch (error) {
      console.error('車種別料金取得エラー:', error);
      // APIエラー時は距離をベースに概算計算
      const standardToll = distance; // 普通車料金
      return {
        // Light_Vehicle_Toll: Math.round(standardToll * 0.8), // 軽自動車は普通車の約80%
        Standard_Vehicle_Toll: standardToll,
        Medium_Vehicle_Toll: Math.round(standardToll * 1.2), // 中型車は普通車の約120%
        // Large_Vehicle_Toll: Math.round(standardToll * 1.4)  // 大型車は普通車の約140%
      };
    }
  },

  // 未完了工事リストの取得
  getIncompleteWorks: async (): Promise<IncompleteWork[]> => {
    try {
      const response = await api.get<IncompleteWorksResponse>('/construction/incomplete/list');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '未完了工事リストの取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      console.error('未完了工事リストの取得に失敗:', error);
      throw error;
    }
  },

  // 工事を完了状態に更新
  completeWork: async (constructionId: number): Promise<boolean> => {
    try {
      const response = await api.put<CompleteWorkResponse>(`/construction/incomplete/${constructionId}/complete`);
      if (!response.data.success) {
        throw new Error(response.data.error || '工事の完了状態への更新に失敗しました');
      }
      return true;
    } catch (error) {
      console.error('工事の完了状態への更新に失敗:', error);
      throw error;
    }
  },

  // 請求書依頼未作成の完了工事リストの取得
  getCompletedWorksWithoutInvoiceRequest: async (): Promise<CompletedWorkWithoutInvoiceRequest[]> => {
    try {
      const response = await api.get<CompletedWorksWithoutInvoiceRequestResponse>(
        '/construction/completed/without-invoice-request'
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '請求書依頼未作成の完了工事リストの取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      console.error('請求書依頼未作成の完了工事リストの取得に失敗:', error);
      throw error;
    }
  },

  // ユーザーIDを指定して請求書依頼未作成の完了工事リストを取得
  getCompletedWorksWithoutInvoiceRequestByUserId: async (userName: string): Promise<CompletedWorkWithoutInvoiceRequest[]> => {
    try {
      // console.log('請求書依頼未作成の完了工事リスト取得: リクエスト開始', { userName });
  
      const response = await api.get<CompletedWorksWithoutInvoiceRequestResponse>(
        '/construction/completed/without-invoice-request',
        { params: { userName } }
      );
  
      // console.log('請求書依頼未作成の完了工事リスト取得: レスポンス受信', response.data);
  
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || '請求書依頼未作成の完了工事リストの取得に失敗しました');
      }
  
      // ここがキーポイント：バックエンドのレスポンスデータをフロントエンドの型にマッピングする
      const mappedData: CompletedWorkWithoutInvoiceRequest[] = response.data.data.map(item => ({
        ID: item.ID, // バックエンドではworkId、フロントではID
        customerName: item.customerName,
        siteName: item.siteName,
        constructionDetails: item.workContent || '',// バックエンドではworkContent、フロントではconstructionDetails
        estimatedAmount: item.estimatedAmount || 0,
        supervisorId: item.supervisorId || 0,
        // 日報データから取得した日付
        startDate: item.startDate,
        startDate_str: item.startDate_str,
        completionDate: item.completionDate, // 日報の最新日付
        completionDate_str: item.completionDate_str,
        // 工事テーブルの完了日も保持（必要に応じて）
        // lastUpdatedDate: item.lastUpdatedDate,
        // lastUpdatedDate_str: item.lastUpdatedDate_str,
        workContent: item.workContent // 工事内容を保持
      }));

      return mappedData;
    } catch (error) {
      console.error('請求書依頼未作成の完了工事リストの取得に失敗:', error);
      throw error;
    }
  },
  
  // ユーザーIDを指定して請求書依頼を作成
  createInvoiceRequestWithUserId: async (constructionId: number, userId: string): Promise<boolean> => {
    try {
      // console.log('請求書依頼作成: リクエスト開始', { constructionId, userId });

      const response = await api.post<CreateInvoiceRequestResponse>(
        `/construction/${constructionId}/invoice-request`,
        { userId }
      );

      // console.log('請求書依頼作成: レスポンス受信', response.data);

      if (!response.data.success) {
        throw new Error(response.data.error || '請求書依頼の作成に失敗しました');
      }
      return true;
    } catch (error) {
      console.error('請求書依頼の作成に失敗:', error);
      throw error;
    }
  },

  // 請求書依頼作成
  createInvoiceRequest: async (constructionId: number): Promise<boolean> => {
    try {
      const response = await api.post<CreateInvoiceRequestResponse>(
        `/construction/${constructionId}/invoice-request`
      );
      if (!response.data.success) {
        throw new Error(response.data.error || '請求書依頼の作成に失敗しました');
      }
      return true;
    } catch (error) {
      console.error('請求書依頼の作成に失敗:', error);
      throw error;
    }
  },

  // 週の初回ログインチェック
  checkWeeklyLogin: async (): Promise<boolean> => {
    try {
      // バックエンドAPIを呼び出す（認証確認用）
      // console.log('週初回ログインチェック: APIリクエスト開始');
      const response = await api.get<WeeklyLoginCheckResponse>('/users/weekly-login-check');
      // console.log('週初回ログインチェック: APIレスポンス受信', response.data);

      if (!response.data.success) {
        // console.log('週初回ログインチェック: API失敗');
        return false;
      }

      // 認証が成功したら、LocalStorageを使って週初回ログインをチェック
      const username = 'current_user'; // 実際のユーザー名またはIDを使用する場合は変更
      const storageKey = `lastLogin_${username}`;
      const lastLoginStr = localStorage.getItem(storageKey);
      // console.log('週初回ログインチェック: 前回ログイン日時', lastLoginStr || 'なし（初回ログイン）');

      const now = new Date();
      // console.log('週初回ログインチェック: 現在日時', now.toString());

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      // console.log('週初回ログインチェック: 今日の0時', new Date(today).toString());

      // 最終ログイン日がない場合は初回とみなす
      if (!lastLoginStr) {
        // console.log('週初回ログインチェック: 初回ログイン確認');
        localStorage.setItem(storageKey, today.toString());
        return true;
      }

      const lastLoginDate = new Date(parseInt(lastLoginStr));
      // console.log('週初回ログインチェック: 前回ログイン日', lastLoginDate.toString());

      const lastLoginDay = lastLoginDate.getDay(); // 0-6 (日-土)
      const currentDay = now.getDay();
      // console.log('週初回ログインチェック: 前回の曜日', lastLoginDay, '今日の曜日', currentDay);

      // 週初回ログインの判定ロジック:
      // 1. 現在が月曜日(1)で、最終ログインが月曜日でない
      // 2. 最終ログインが日付的に7日以上前
      const isMonday = currentDay === 1;
      // console.log('週初回ログインチェック: 今日は月曜日か', isMonday);

      const daysSinceLastLogin = (today - parseInt(lastLoginStr)) / (1000 * 60 * 60 * 24);
      // console.log('週初回ログインチェック: 前回ログインからの経過日数', daysSinceLastLogin);

      const isFirstLoginOfWeek =
        (isMonday && lastLoginDay !== 1) || // 月曜日に初めてログインした
        daysSinceLastLogin >= 7; // 7日以上経過

      // console.log('週初回ログインチェック: 週初回ログイン判定', isFirstLoginOfWeek, {
      //   条件1: `月曜日チェック: ${isMonday && lastLoginDay !== 1}`,
      //   条件2: `7日以上経過: ${daysSinceLastLogin >= 7}`
      // });

      // 今日の日付を最終ログイン日として保存
      localStorage.setItem(storageKey, today.toString());
      // console.log('週初回ログインチェック: 最終ログイン日を更新', today.toString());

      return isFirstLoginOfWeek;
    } catch (error) {
      console.error('週の初回ログインチェックに失敗:', error);
      return false; // エラーの場合はfalseを返す
    }
  }



};